import { useEffect, useState } from 'react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/shared/components/molecules/AlertDialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { CalendarEvent } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { toLocalISOString } from '../../utils/shared'

interface EventDeleteFormProps {
	event?: CalendarEvent
	title?: string
}

/**
 * Calculates the next valid occurrence date based on RRULE
 * Used when splitting timeslots after deleting an instance
 */
const calculateNextOccurrence = (fromDate: Date, rrule: any): Date => {
	const result = new Date(fromDate)

	if (!rrule || !rrule.byweekday) {
		result.setDate(result.getDate() + 1)
		return result
	}

	const validDays = new Set(
		Array.isArray(rrule.byweekday) ? rrule.byweekday : [rrule.byweekday]
	)

	for (let i = 1; i <= 7; i++) {
		const testDate = new Date(fromDate)
		testDate.setDate(testDate.getDate() + i)

		const dayOfWeek = testDate.getDay()
		const rruleDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1

		if (validDays.has(rruleDayOfWeek)) {
			return testDate
		}
	}

	result.setDate(result.getDate() + 1)
	return result
}

/**
 * Generates a unique ID for a timeslot
 */
const generateTimeslotId = (): string => {
	return `timeslot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const EventDeleteForm = ({ event, title }: EventDeleteFormProps) => {
	const { t } = useTranslation()
	const {
		events,
		addEvent,
		deleteEvent,
		eventDeleteOpen,
		setEventDeleteOpen,
		setEventViewOpen,
	} = useEvents()
	const { showNotification } = useNotificationContext()
	const [deleteOption, setDeleteOption] = useState<
		'this' | 'following' | 'all'
	>('this')

	const originalEvent = events.find((e) => e.id === event?.id)
	const isRecurring =
		!!originalEvent?.rrule ||
		(originalEvent?.repeatDays && originalEvent.repeatDays.length > 0)

	useEffect(() => {
		if (eventDeleteOpen) {
			setDeleteOption('this')
		}
	}, [eventDeleteOpen])

	function closeDialogs() {
		setEventDeleteOpen(false)
		setEventViewOpen(false)
	}

	function showDeleteSuccess() {
		const eventTitle = title ?? ''

		showNotification({
			type: 'success',
			message: t('entities.schedule.notifications.event_deleted_title'),
			description: t(
				'entities.schedule.notifications.event_deleted_description',
				{
					title: eventTitle,
				}
			),
		})
	}

	function handleDelete() {
		if (!event) return

		const effectiveDeleteOption = isRecurring ? deleteOption : 'this'

		switch (effectiveDeleteOption) {
			case 'this':
				if (!originalEvent?.rrule) {
					deleteEvent(event.id)
					showDeleteSuccess()
					break
				}

				if (event.start && originalEvent?.rrule) {
					const deleteDate = new Date(event.start)

					const dtstartDate = new Date(originalEvent.rrule.dtstart)
					const hours = dtstartDate.getHours()
					const minutes = dtstartDate.getMinutes()
					const seconds = dtstartDate.getSeconds()

					deleteDate.setHours(hours, minutes, seconds, 0)

					const dayBeforeDelete = new Date(deleteDate)
					dayBeforeDelete.setDate(dayBeforeDelete.getDate() - 1)
					dayBeforeDelete.setHours(23, 59, 59, 999)

					const nextOccurrence = calculateNextOccurrence(
						deleteDate,
						originalEvent.rrule
					)
					nextOccurrence.setHours(hours, minutes, seconds, 0)

					const cleanRRule = {
						freq: originalEvent.rrule.freq,
						...(originalEvent.rrule.byweekday
							? { byweekday: originalEvent.rrule.byweekday }
							: {}),
						...(originalEvent.rrule.until &&
						originalEvent.rrule.until !== toLocalISOString(dayBeforeDelete)
							? { until: originalEvent.rrule.until }
							: {}),
					}

					const timeslot1: CalendarEvent = {
						...originalEvent,
						id: generateTimeslotId(),
						rrule: {
							...cleanRRule,
							dtstart: originalEvent.rrule.dtstart,
							until: toLocalISOString(dayBeforeDelete),
						},
					}

					const timeslot2: CalendarEvent = {
						...originalEvent,
						id: generateTimeslotId(),
						rrule: {
							...cleanRRule,
							dtstart: toLocalISOString(nextOccurrence),
							...(originalEvent.rrule.until
								? { until: originalEvent.rrule.until }
								: {}),
						},
					}

					deleteEvent(originalEvent.id)

					if (dayBeforeDelete.getTime() >= dtstartDate.getTime()) {
						addEvent(timeslot1)
					}

					let isPart2Valid = true
					if (originalEvent.rrule.until) {
						const originalUntilDate = new Date(originalEvent.rrule.until)
						if (nextOccurrence.getTime() > originalUntilDate.getTime()) {
							isPart2Valid = false
						}
					}

					if (isPart2Valid) {
						addEvent(timeslot2)
					}

					showNotification({
						type: 'success',
						message: t('entities.schedule.calendar.event_instance_excluded'),
						description: t(
							'entities.schedule.calendar.timeslot_split_description'
						),
					})
				}
				break
			case 'following':
				if (event.start && originalEvent?.rrule) {
					const untilDate = new Date(event.start)
					untilDate.setDate(untilDate.getDate() - 1)

					const recurrenceStart = new Date(originalEvent.rrule.dtstart)
					if (untilDate.getTime() < recurrenceStart.getTime()) {
						deleteEvent(originalEvent.id)
						showDeleteSuccess()
						break
					}

					const formattedUntil = toLocalISOString(untilDate)

					deleteEvent(originalEvent.id)
					const updatedEvent = {
						...originalEvent,
						rrule: {
							...originalEvent.rrule,
							until: formattedUntil,
						},
					}

					addEvent(updatedEvent)
					showDeleteSuccess()
				}
				break
			case 'all':
				deleteEvent(event.id)
				showDeleteSuccess()
				break
		}

		closeDialogs()
	}

	return (
		<AlertDialog open={eventDeleteOpen}>
			<AlertDialogTrigger asChild>
				<Button
					variant="destructive"
					onClick={() => setEventDeleteOpen(true)}
					className="rounded-full"
				>
					{t('actions.delete')}
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					{isRecurring ? (
						<>
							<AlertDialogTitle>
								{t('entities.schedule.delete_dialog.title')}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t('entities.schedule.delete_dialog.description')}
							</AlertDialogDescription>
						</>
					) : (
						<>
							<AlertDialogTitle className="flex flex-row justify-between items-center">
								<h1>
									{title
										? t(
												'entities.schedule.event_delete.dialog_title_with_name',
												{
													title,
												}
											)
										: t('entities.schedule.event_delete.dialog_title')}
								</h1>
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t('entities.schedule.event_delete.dialog_description')}
							</AlertDialogDescription>
						</>
					)}
				</AlertDialogHeader>
				{isRecurring && (
					<div className="py-4 space-y-3">
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('this')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'this' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'this' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_event')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('following')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'following' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'following' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_and_following')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('all')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'all' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'all' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.all_events')}
							</span>
						</div>
					</div>
				)}
				<AlertDialogFooter>
					<AlertDialogCancel onClick={() => setEventDeleteOpen(false)}>
						{t('actions.back')}
					</AlertDialogCancel>
					<Button variant="destructive" onClick={handleDelete}>
						{t('actions.delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
